import { PaginationState } from '@tanstack/react-table'
import { Box } from 'features/knowledgeDocumentViewer/components/viewer/shared/types'
import { useBoxHover } from 'features/knowledgeDocumentViewer/hooks/useBoxHover'
import { useTabState } from 'features/knowledgeDocumentViewer/hooks/useTabState'
import {
  DocumentHistoryItem,
  DocumentTab,
  FilterOption,
  type FragmentHighlight,
  type RegionHighlight,
  SearchHistoryItem,
  type SearchStateUpdate,
  TabHistoryItem,
  TimeSeriesHistoryItem,
  TooltipOption,
  ViewLayout,
} from 'features/knowledgeDocumentViewer/types'
import { DocumentViewerAssetType } from 'features/knowledgeDocumentViewer/types/ocrViewer'
import { createDefaultSearchHistoryItem } from 'features/knowledgeDocumentViewer/utils/tabStateUrlHelpers'
import {
  getViewerPreferences,
  type LinkHighlightOptions,
  updateViewerPreferences,
  VIEWER_PREFERENCES_DEFAULTS,
} from 'features/knowledgeDocumentViewer/utils/viewerPreferencesStorage'
import { useGeckoPermissions } from 'features/permissions'
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { DocumentViewerUrlPaths } from 'routes/documents/-searchParams'
import { PERMISSION_GECKO_INTERNAL_MEMBER } from 'types'

const MAX_RECENT_VIEWER_ACTIONS = 20

export enum ActionMode {
  None = 'none',
  Assign = 'assign',
  Remove = 'remove',
  Group = 'group',
  Split = 'split',
  Info = 'info',
  Link = 'link',
}

interface KnowledgeDocumentUXContextType {
  actionMode: ActionMode
  setActionMode: (actionMode: ActionMode) => void
  selectedAssetType: DocumentViewerAssetType | undefined
  setSelectedAssetType: (assetType: DocumentViewerAssetType | undefined) => void
  groupDelimiter: string | undefined
  setGroupDelimiter: (delimiter: string | undefined) => void
  hoverId: string | null
  handleMouseEnter: (box: Box) => void
  handleMouseLeave: () => void
  openTabs: DocumentTab[]
  addTab: (tab: DocumentTab) => void
  addDocumentTab: (params: {
    documentId: string
    pageNumber?: number
    selectedBoxIds?: string[]
    query?: string
    searchOriginQuery?: string
    fragmentHighlight?: FragmentHighlight
    regionHighlight?: RegionHighlight
  }) => void
  removeTab: (tabId: string) => void
  renameTab: (tabId: string, label: string) => void
  reorderTabs: (newTabs: DocumentTab[]) => void
  activeTabId: string
  setActiveTabId: (tabId: string) => void
  pagination: PaginationState
  setPagination: React.Dispatch<React.SetStateAction<PaginationState>>
  activeHistoryItem: TabHistoryItem
  isOnDocument: boolean
  isOnSearch: boolean
  isOnLinkedDocuments: boolean
  isOnTimeSeries: boolean
  currentSensorIds: string[]
  currentTimeWindow: { from: string; to: string } | undefined
  currentTimeSeriesLabel: string
  currentLinkedDocumentsData: SearchHistoryItem['linkedDocuments'] | null
  currentDocumentId: string
  currentPageNumber: number
  currentSelectedBoxIds: string[]
  currentDocumentQuery: string
  currentSearchOriginQuery: string
  currentSearchState: SearchHistoryItem['searchState']
  tooltipOptions: TooltipOption[]
  setTooltipOptions: React.Dispatch<React.SetStateAction<TooltipOption[]>>
  linkHighlights: LinkHighlightOptions
  setLinkHighlights: React.Dispatch<React.SetStateAction<LinkHighlightOptions>>
  filterOptions: FilterOption
  setFilterOptions: React.Dispatch<React.SetStateAction<FilterOption>>
  assetTypeFilters: DocumentViewerAssetType[]
  setAssetTypeFilters: React.Dispatch<
    React.SetStateAction<DocumentViewerAssetType[]>
  >
  isAdvancedSearchOpen: boolean
  setIsAdvancedSearchOpen: React.Dispatch<React.SetStateAction<boolean>>
  handleSelectDocument: (
    documentId: string,
    query?: string,
    fragmentHighlight?: FragmentHighlight,
    searchOriginQuery?: string
  ) => void
  isFullscreen: boolean
  isSidebarCollapsed: boolean
  setIsSidebarCollapsed: React.Dispatch<React.SetStateAction<boolean>>
  viewOnly: boolean
  pushToHistory: (tabId: string, item: TabHistoryItem) => void
  goBack: (tabId: string) => void
  goForward: (tabId: string) => void
  updateDocumentHistoryItem: (
    tabId: string,
    updates: Partial<Omit<DocumentHistoryItem, 'type'>>
  ) => void
  updateSearchHistoryItem: (
    tabId: string,
    searchStateUpdates: SearchStateUpdate
  ) => void
  updateTimeSeriesHistoryItem: (
    tabId: string,
    updates: Partial<Omit<TimeSeriesHistoryItem, 'type'>>
  ) => void
  addSensorsToTimeSeriesTab: (tabId: string, sensorIds: string[]) => void
  setTimeSeriesTabSensors: (
    tabId: string,
    sensorIds: string[],
    options?: {
      sensorColors?: Record<string, string>
      deemphasizedSensorIds?: string[]
    }
  ) => void
  prependPinnedTimeSeriesTab: (tab: DocumentTab) => void
  moveTabToFront: (tabId: string) => void
  canGoBack: boolean
  canGoForward: boolean
  isGeckoUser: boolean
  viewLayout: ViewLayout
  setViewLayout: React.Dispatch<React.SetStateAction<ViewLayout>>
  siteId: string
  tabIndicatorColors: Map<string, string>
  setTabIndicatorColors: React.Dispatch<
    React.SetStateAction<Map<string, string>>
  >
  recentViewerActions: string[]
  recordViewerAction: (action: string) => void
}

interface KnowledgeDocumentUXProviderProps {
  children: React.ReactNode
  basePath: DocumentViewerUrlPaths
  viewOnly: boolean
  siteId: string
}

export const KnowledgeDocumentUXProvider: React.FC<
  KnowledgeDocumentUXProviderProps
> = ({ children, basePath, viewOnly, siteId }) => {
  const {
    openTabs,
    activeTabId,
    setActiveTabId,
    addTab,
    addDocumentTab,
    removeTab,
    renameTab,
    reorderTabs,
    activeHistoryItem,
    pushToHistory,
    goBack,
    goForward,
    updateDocumentHistoryItem,
    updateSearchHistoryItem,
    updateTimeSeriesHistoryItem,
    addSensorsToTimeSeriesTab,
    setTimeSeriesTabSensors,
    prependPinnedTimeSeriesTab,
    moveTabToFront,
    canGoBack,
    canGoForward,
  } = useTabState(basePath, siteId)

  const { hasPermissions, user } = useGeckoPermissions()
  const isGeckoUser = hasPermissions([PERMISSION_GECKO_INTERNAL_MEMBER])
  const userId = user?.id

  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 200,
  })

  const [persistedUXPrefs] = useState(() =>
    userId ? getViewerPreferences(userId, siteId) : null
  )

  const [actionMode, setActionMode] = useState<ActionMode>(ActionMode.Info)
  const [tooltipOptions, setTooltipOptions] = useState<TooltipOption[]>(
    persistedUXPrefs?.tooltipOptions ?? [TooltipOption.ShowTooltips]
  )
  const [linkHighlights, setLinkHighlights] = useState<LinkHighlightOptions>(
    persistedUXPrefs?.linkHighlights ??
      VIEWER_PREFERENCES_DEFAULTS.linkHighlights
  )
  const [selectedAssetType, setSelectedAssetType] = useState<
    DocumentViewerAssetType | undefined
  >(undefined)
  const [groupDelimiter, setGroupDelimiter] = useState<string | undefined>(
    undefined
  )
  const [filterOptions, setFilterOptions] = useState<FilterOption>(
    persistedUXPrefs?.filterOptions ?? FilterOption.ShowConfirmed
  )
  const [assetTypeFilters, setAssetTypeFilters] = useState<
    DocumentViewerAssetType[]
  >([])
  const [isAdvancedSearchOpen, setIsAdvancedSearchOpen] =
    useState<boolean>(false)
  const [tabIndicatorColors, setTabIndicatorColors] = useState<
    Map<string, string>
  >(() => new Map())
  const [recentViewerActions, setRecentViewerActions] = useState<string[]>([])

  const [viewLayout, setViewLayout] = useState<ViewLayout>(
    persistedUXPrefs?.viewLayout ?? 'continuous'
  )

  const [isFullscreen, setIsFullscreen] = useState(false)
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(
    persistedUXPrefs?.isSidebarCollapsed ?? true
  )

  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement)
    }

    document.addEventListener('fullscreenchange', handleFullscreenChange)
    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange)
    }
  }, [])

  const uxPrefsRef = useRef({
    tooltipOptions,
    filterOptions,
    isSidebarCollapsed,
    viewLayout,
    linkHighlights,
  })
  uxPrefsRef.current = {
    tooltipOptions,
    filterOptions,
    isSidebarCollapsed,
    viewLayout,
    linkHighlights,
  }

  const userIdRef = useRef(userId)
  userIdRef.current = userId

  useEffect(() => {
    if (!userId) return
    const timer = setTimeout(() => {
      updateViewerPreferences(userId, {
        tooltipOptions,
        filterOptions,
        isSidebarCollapsed,
        viewLayout,
        linkHighlights,
      })
    }, 300)
    return () => clearTimeout(timer)
  }, [
    userId,
    tooltipOptions,
    filterOptions,
    isSidebarCollapsed,
    viewLayout,
    linkHighlights,
  ])

  useEffect(() => {
    return () => {
      const uid = userIdRef.current
      if (!uid) return
      const {
        tooltipOptions: t,
        filterOptions: f,
        isSidebarCollapsed: s,
        viewLayout: v,
        linkHighlights: l,
      } = uxPrefsRef.current
      updateViewerPreferences(uid, {
        tooltipOptions: t,
        filterOptions: f,
        isSidebarCollapsed: s,
        viewLayout: v,
        linkHighlights: l,
      })
    }
  }, [])

  const { hoverId, handleMouseEnter, handleMouseLeave } = useBoxHover()

  const isOnDocument = activeHistoryItem.type === 'document'
  const isOnLinkedDocuments =
    activeHistoryItem.type === 'search' &&
    activeHistoryItem.linkedDocuments !== undefined
  const isOnSearch = activeHistoryItem.type === 'search' && !isOnLinkedDocuments
  const isOnTimeSeries = activeHistoryItem.type === 'timeseries'
  const currentLinkedDocumentsData =
    activeHistoryItem.type === 'search'
      ? (activeHistoryItem.linkedDocuments ?? null)
      : null
  const currentDocumentId = isOnDocument ? activeHistoryItem.documentId : ''
  const currentPageNumber = isOnDocument ? activeHistoryItem.pageNumber : 1
  const currentSelectedBoxIds = useMemo(
    () => (isOnDocument ? activeHistoryItem.selectedBoxIds : []),
    [isOnDocument, activeHistoryItem]
  )
  const currentDocumentQuery = isOnDocument ? activeHistoryItem.query : ''
  const currentSearchOriginQuery = isOnDocument
    ? (activeHistoryItem.searchOriginQuery ?? '')
    : ''
  const defaultSearchState = useMemo(
    () => createDefaultSearchHistoryItem().searchState,
    []
  )
  const currentSearchState =
    activeHistoryItem.type === 'search'
      ? activeHistoryItem.searchState
      : defaultSearchState
  const currentSensorIds = useMemo(
    () =>
      isOnTimeSeries
        ? (activeHistoryItem as TimeSeriesHistoryItem).sensorIds
        : [],
    [isOnTimeSeries, activeHistoryItem]
  )
  const currentTimeWindow = useMemo(
    () =>
      isOnTimeSeries
        ? (activeHistoryItem as TimeSeriesHistoryItem).timeWindow
        : undefined,
    [isOnTimeSeries, activeHistoryItem]
  )
  const currentTimeSeriesLabel = isOnTimeSeries
    ? (activeHistoryItem as TimeSeriesHistoryItem).label
    : ''

  const handleSelectDocument = useCallback(
    (
      documentId: string,
      query?: string,
      fragmentHighlight?: FragmentHighlight,
      searchOriginQuery?: string
    ) => {
      const pageNumber = fragmentHighlight?.pageNum ?? 1
      if (
        tooltipOptions.includes(TooltipOption.AutoOpenNewTab) &&
        isOnDocument &&
        currentDocumentId !== ''
      ) {
        addDocumentTab({
          documentId,
          pageNumber,
          query,
          searchOriginQuery,
          fragmentHighlight,
        })
      } else {
        pushToHistory(activeTabId, {
          type: 'document',
          documentId,
          pageNumber,
          selectedBoxIds: [],
          query: query ?? '',
          ...(searchOriginQuery && { searchOriginQuery }),
          ...(fragmentHighlight && { fragmentHighlight }),
        })
      }
    },
    [
      tooltipOptions,
      isOnDocument,
      currentDocumentId,
      addDocumentTab,
      pushToHistory,
      activeTabId,
    ]
  )

  const recordViewerAction = useCallback((action: string) => {
    setRecentViewerActions((prev) => {
      if (prev[prev.length - 1] === action) return prev
      return [...prev, action].slice(-MAX_RECENT_VIEWER_ACTIONS)
    })
  }, [])

  const contextValue = useMemo<KnowledgeDocumentUXContextType>(
    () => ({
      actionMode,
      setActionMode,
      selectedAssetType,
      setSelectedAssetType,
      groupDelimiter,
      setGroupDelimiter,
      hoverId,
      handleMouseEnter,
      handleMouseLeave,
      openTabs,
      addTab,
      addDocumentTab,
      removeTab,
      renameTab,
      reorderTabs,
      activeTabId,
      setActiveTabId,
      pagination,
      setPagination,
      activeHistoryItem,
      isOnDocument,
      isOnSearch,
      isOnLinkedDocuments,
      isOnTimeSeries,
      currentSensorIds,
      currentTimeWindow,
      currentTimeSeriesLabel,
      currentLinkedDocumentsData,
      currentDocumentId,
      currentPageNumber,
      currentSelectedBoxIds,
      currentDocumentQuery,
      currentSearchOriginQuery,
      currentSearchState,
      tooltipOptions,
      setTooltipOptions,
      linkHighlights,
      setLinkHighlights,
      filterOptions,
      setFilterOptions,
      assetTypeFilters,
      setAssetTypeFilters,
      isAdvancedSearchOpen,
      setIsAdvancedSearchOpen,
      handleSelectDocument,
      isFullscreen,
      isSidebarCollapsed,
      setIsSidebarCollapsed,
      viewOnly,
      pushToHistory,
      goBack,
      goForward,
      updateDocumentHistoryItem,
      updateSearchHistoryItem,
      updateTimeSeriesHistoryItem,
      addSensorsToTimeSeriesTab,
      setTimeSeriesTabSensors,
      prependPinnedTimeSeriesTab,
      moveTabToFront,
      canGoBack,
      canGoForward,
      viewLayout,
      setViewLayout,
      isGeckoUser,
      siteId,
      tabIndicatorColors,
      setTabIndicatorColors,
      recentViewerActions,
      recordViewerAction,
    }),
    [
      actionMode,
      selectedAssetType,
      groupDelimiter,
      hoverId,
      handleMouseEnter,
      handleMouseLeave,
      openTabs,
      addTab,
      addDocumentTab,
      removeTab,
      renameTab,
      reorderTabs,
      activeTabId,
      setActiveTabId,
      pagination,
      activeHistoryItem,
      isOnDocument,
      isOnSearch,
      isOnLinkedDocuments,
      isOnTimeSeries,
      currentSensorIds,
      currentTimeWindow,
      currentTimeSeriesLabel,
      currentLinkedDocumentsData,
      currentDocumentId,
      currentPageNumber,
      currentSelectedBoxIds,
      currentDocumentQuery,
      currentSearchOriginQuery,
      currentSearchState,
      tooltipOptions,
      linkHighlights,
      filterOptions,
      assetTypeFilters,
      isAdvancedSearchOpen,
      handleSelectDocument,
      isFullscreen,
      isSidebarCollapsed,
      viewOnly,
      pushToHistory,
      goBack,
      goForward,
      updateDocumentHistoryItem,
      updateSearchHistoryItem,
      updateTimeSeriesHistoryItem,
      addSensorsToTimeSeriesTab,
      setTimeSeriesTabSensors,
      prependPinnedTimeSeriesTab,
      moveTabToFront,
      canGoBack,
      canGoForward,
      viewLayout,
      isGeckoUser,
      siteId,
      tabIndicatorColors,
      recentViewerActions,
      recordViewerAction,
    ]
  )

  return (
    <KnowledgeDocumentUXContext.Provider value={contextValue}>
      {children}
    </KnowledgeDocumentUXContext.Provider>
  )
}

const KnowledgeDocumentUXContext = createContext<
  KnowledgeDocumentUXContextType | undefined
>(undefined)

export const useKnowledgeDocumentUXContext = () => {
  const context = useContext(KnowledgeDocumentUXContext)
  if (context === undefined) {
    throw new Error(
      'useKnowledgeDocumentUXContext must be used within a KnowledgeDocumentUXProvider'
    )
  }
  return context
}

/**
 * Returns the document UX context if available, otherwise undefined.
 * Use this when components may or may not be rendered inside a document viewer.
 */
export const useKnowledgeDocumentUXContextOptional = () => {
  return useContext(KnowledgeDocumentUXContext)
}
